import { useEffect, useReducer } from "react";
import CustomModal from "components/CustomModal";
import { imageTypes } from "./constants";
import { Button } from "reactstrap";
import { useMutation } from "react-query";
import { CustomAlert } from "components";
import { useFieldArray, useForm } from "react-hook-form";
import apiService from "services/api";
import { ImageUploadv2 } from "components/CustomUpload/ImageUploadv2";
import React from "react";
import { FiTrash } from "react-icons/fi";
import { ProtectedImg } from "components";
import { useNotificationContext } from "contexts";

const AssetUploader = ({
  imageType,
  labelText,
  control,
  studentRecordId,
  setStaged,
}) => {
  const { mutate: removeAsset, isLoading } = useMutation({
    mutationFn: async (data) => {
      console.info(data);
      if (data.new) return data.idx;
      await apiService.lessonRecord.studentRecord.deleteAsset(data.id);
      return data?.idx;
    },
    onSuccess: (index) => {
      remove(index);
      setDeleteConfirmationModal(null);
    },
  });

  const { append, fields, remove } = useFieldArray({
    control,
    name: "lessonFiles",
    keyName: "_id",
  });
  const [deleteConfirmationModal, setDeleteConfirmationModal] =
    React.useState(null);
  const ref = React.createRef();
  const items = React.useMemo(
    () =>
      fields
        .map((field, idx) => ({ ...field, idx }))
        .filter((field) => field.type === imageType),
    [fields, imageType],
  );

  return (
    <div
      className="px-3 pt-2 pb-3"
      style={{
        border: "2px solid #ffffff25",
        borderRadius: 8,
      }}
    >
      <CustomAlert
        title="Remove Record"
        show={!!deleteConfirmationModal}
        hideModal={() => setDeleteConfirmationModal(null)}
        description={<>Are you sure you want to remove entry </>}
        disabled={isLoading}
        submitModal={() =>
          removeAsset(deleteConfirmationModal, deleteConfirmationModal?.idx)
        }
        submitButtonText="Delete"
      />
      <div>
        <div className="text-primary font-weight-bold">{labelText}</div>
        <ImageUploadv2
          fileInputRef={ref}
          setValue={(_, imgURI) => {
            setStaged(true);
            return append({
              new: true,
              type: imageType,
              data: imgURI,
              studentRecordId,
            });
          }}
          labelText={`Click to upload ${labelText}`}
          disableImgGallery
        />
      </div>

      <div
        className="d-inline-flex align-items-start mt-3 pb-2"
        style={{ maxWidth: "100%", overflowX: "auto", gap: 10 }}
      >
        {items?.length ? (
          items.map((field) => {
            const isPdf =
              field.filepath?.endsWith(".pdf") ||
              field?.data?.slice(0, 10)?.includes("pdf");

            return (
              <div
                className="position-relative"
                style={{ flexShrink: 0 }}
                key={field._id}
              >
                <ProtectedImg
                  src={field.filepath || field.data}
                  className="rounded"
                  height={200}
                />
                <Button
                  size="sm"
                  className="btn-icon bg-danger btn-link edit my-0"
                  type="button"
                  style={
                    isPdf
                      ? {}
                      : {
                          position: "absolute",
                          top: 4,
                          right: 4,
                        }
                  }
                  onClick={() => {
                    setDeleteConfirmationModal(field);
                  }}
                >
                  <FiTrash size={20} className="text-white" />
                </Button>
              </div>
            );
          })
        ) : (
          <small className="text-muted">No images uploaded yet</small>
        )}
      </div>
    </div>
  );
};

export const UploadStudentRecordModal = ({
  show,
  studentRecord,
  hideModal,
  refetch,
}) => {
  const { register, control, handleSubmit, reset, getValues } = useForm({
    defaultValues: {
      lessonFiles: [],
    },
  });

  React.useEffect(() => {
    reset({
      lessonFiles: studentRecord.lessonFiles,
    });
  }, [studentRecord.lessonFiles, reset]);

  const { update } = useFieldArray({
    control,
    name: "lessonFiles",
    keyName: "_id",
  });

  const { createNotification } = useNotificationContext();
  const [staged, setStaged] = React.useState(false);

  const { mutate: updateAssets, isLoading } = useMutation({
    mutationFn: (data) => {
      const newAssets = data?.lessonFiles
        ?.map((file, idx) => ({ ...file, idx }))
        .filter((file) => file.new);
      return apiService.lessonRecord.studentRecord
        .updateAssets({
          lessonFiles: newAssets,
        })
        .then(() => newAssets?.map((asset) => asset.idx));
    },
    onSuccess: (newAssetsIdxs) => {
      setStaged(false);
      newAssetsIdxs?.forEach((assetIdx) => {
        update(assetIdx, {
          ...getValues("lessonFiles")?.[assetIdx],
          new: false,
        });
      });
      createNotification({
        type: "success",
        message: "Uploaded assets successfully",
      });
    },
  });

  React.useEffect(() => {
    if (isLoading) {
      createNotification({
        type: "info",
        message: "Uploading assets...",
      });
    }
  }, [isLoading, createNotification]);

  useEffect(() => {
    if (!show) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const studentDetail = studentRecord?.studentDetail;

  return (
    <CustomModal
      show={show}
      hideModal={hideModal}
      buttons={
        <>
          <Button
            type="submit"
            form="saveStudentRecordAssetEdits"
            color="success"
            disabled={isLoading}
          >
            Save
          </Button>
        </>
      }
      title={[
        `[${studentRecord?.lessonDetail?.date}]`,
        studentDetail?.firstName,
        studentDetail?.lastName,
        studentDetail?.lessonDetail?.lesson?.name,
      ].join(" ")}
      triggerAlertWhenClose={staged}
      triggerAlertMessageWhenClose="Confirm close? Changes will be discarded"
    >
      <form
        onSubmit={handleSubmit(updateAssets)}
        id="saveStudentRecordAssetEdits"
      />
      <AssetUploader
        imageType={imageTypes.CLASSWORK}
        labelText="Classwork"
        setStaged={setStaged}
        control={control}
        studentRecordId={studentRecord?.id}
        refetch={refetch}
      />
      <br />
      <AssetUploader
        imageType={imageTypes.IN_CLASS_IMAGES}
        labelText="In Class Photos"
        control={control}
        setStaged={setStaged}
        studentRecordId={studentRecord?.id}
        refetch={refetch}
      />
    </CustomModal>
  );
};

const initialUploadStudentRecordModalState = {
  show: false,
  studentRecord: {},
};

export const uploadStudentRecordModalActions = {
  OPEN: 0,
  HIDE: 1,
};

const uploadStudentRecordModalReducer = (state, action) => {
  switch (action.type) {
    case uploadStudentRecordModalActions.OPEN:
      return {
        ...state,
        show: true,
        studentRecord: {
          ...action.payload,
        },
      };
    case uploadStudentRecordModalActions.HIDE:
      return { ...state, show: false, studentRecord: {} };
    default:
      throw new Error("Invalid Action at addstudentRecordModalReducer");
  }
};

export const useUploadStudentRecordModalReducer = () => {
  return useReducer(
    uploadStudentRecordModalReducer,
    initialUploadStudentRecordModalState,
  );
};
